import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { ModuleId } from '@/constants/modules'
import { MODULE_ICONS } from '@/constants/modules'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  type DialogSize,
} from '@/components/ui/dialog'
import { cn } from '@/utils/cn'

export interface AppModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  size?: DialogSize
  /** Retained for default icon resolution — no rainbow chrome. */
  moduleId?: ModuleId
  icon?: LucideIcon
  children: ReactNode
  footer?: ReactNode
  showClose?: boolean
  /** Hide the module icon in the header */
  hideModuleIcon?: boolean
  headerExtra?: ReactNode
  className?: string
  /** view = compact read-only detail; form = create/edit dialogs */
  variant?: 'form' | 'view'
}

/**
 * Standard ERP centered modal — create, edit, view, and forms.
 * Neutral glass-compatible chrome; module color no longer paints the shell.
 */
export function AppModal({
  open,
  onOpenChange,
  title,
  description,
  size = 'md',
  moduleId,
  icon,
  children,
  footer,
  showClose = true,
  hideModuleIcon = false,
  headerExtra,
  className,
  variant = 'form',
}: AppModalProps) {
  const DefaultIcon = moduleId ? MODULE_ICONS[moduleId] : undefined
  const Icon = icon ?? DefaultIcon
  const isView = variant === 'view'
  const showIcon = !hideModuleIcon && !isView && Boolean(Icon)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size={size}
        showClose={showClose}
        compact={isView}
        className={cn('border border-border', className)}
      >
        <DialogHeader compact={isView}>
          <div className="flex min-w-0 items-center gap-2">
            {showIcon && Icon ? (
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/80 text-muted-foreground">
                <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
              </span>
            ) : null}
            <div className="min-w-0 flex-1">
              <DialogTitle compact={isView}>{title}</DialogTitle>
              {description ? (
                <DialogDescription compact={isView}>{description}</DialogDescription>
              ) : null}
              {headerExtra}
            </div>
          </div>
        </DialogHeader>
        <DialogBody compact={isView}>{children}</DialogBody>
        {footer ? (
          <DialogFooter compact={isView}>{footer}</DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
